import { createTheme } from '@quotient/riq-react-ui/theme';

const colors = {
  yellows: {
    500: '#FFF600',
    600: '#FFDD00'
  },

  whites: {
    100: '#FFF'
  },

  blacks: {
    900: '#000'
  },

  grays: {
    100: '#FEFEFE',
    200: '#EFEFEF',
    300: '#E1E1E1',
    400: '#CDCDCD',
    500: '#BABABA',
    600: '#69696B',
    700: '#444444',
    800: '#353535',
    900: '#040707'
  },

  reds: {
    600: '#E21220'
  },

  primary: 'yellows.500',
  primaryDark: 'yellows.600',
  secondary: 'grays.900',
  text: {
    heading: 'secondary',
    subtle: 'grays.600',
    subtleDark: 'grays.800',
    body: 'grays.700',
    link: 'grays.900',
    linkHover: 'grays.900'
  },
  error: {
    text: 'reds.600',
    border: 'reds.600'
  },
  cartCounter: {
    empty: 'secondary',
    nonempty: 'secondary'
  },
  shoppinglist: {
    activeBg: 'secondary',
    inactiveBg: 'grays.600',
    activeColor: 'white',
    inactiveColor: 'white',
    podSeparatorColor: 'grays.400'
  }
};

const { tokens, components } = createTheme({
  mode: 'light',
  tokens: {
    colors,
    fontSizes: {
      0: 0,
      1: '10px',
      2: '12px',
      3: '14px',
      4: '16px',
      5: '18px',
      6: '20px',
      7: '22px',
      8: '24px',
      9: '28px',
      10: '32px',
      Heading: {
        page: 10, // reads from tokens.fontSizes.8
        section: 8,
        paragraph: 3
      }
    },
    fonts: {
      primary: 'Montserrat,sans-serif',
      secondary: 'Gotham-Book'
    },
    lineHeights: {
      adjustFontHeight: '47px',
      compact: '1.3'
    }
  },
  components: {
    Global: {
      body: {
        fontSize: 4
      }
    },
    Text: {
      fontSize: 3,
      variants: {
        default: {
          color: 'text.body'
        }
      }
    },
    Button: {
      sizes: { small: 9, medium: 10, large: 11 },
      fontSize: [2, 3],
      border: 'none',
      ':hover, :focus, :active': {
        textDecoration: 'none'
      },
      ':focus': {
        outline: 'dotted',
        outlineColor: 'reds.600'
      },
      '&:disabled': {
        cursor: 'not-allowed',
        backgroundColor: colors.grays[900],
        color: colors.whites[100]
      },
      variants: {
        primary: {
          backgroundColor: 'primary',
          color: 'secondary',
          borderRadius: 0,
          fontWeight: 'bold',
          ':hover:not(:disabled)': {
            backgroundColor: 'primary',
            color: 'secondary'
          },
          ':active': {
            color: 'primary'
          }
        },
        secondary: {
          backgroundColor: 'secondary',
          color: 'white',
          borderColor: 'secondary',
          borderRadius: 0,
          textDecoration: 'none',
          ':hover, :focus, :active': {
            color: 'white',
            textDecoration: 'none'
          }
        },
        link: {
          ':hover:not(:disabled)': {
            color: 'white',
            textDecoration: 'underline'
          }
        },
        subtle: {
          backgroundColor: 'secondary'
        }
      }
    },

    Link: {
      color: 'text.link',
      textDecoration: 'underline',
      ':hover': {
        color: 'text.linkHover'
      },
      variants: {
        default: { color: 'inherit' },
        primary: { color: 'primary' },
        subtle: {
          color: 'grey',
          ':hover': {
            color: 'text.linkHover'
          }
        },
        heading: { color: 'text.heading' },
        body: { color: 'text.body' },
        subtleDark: { color: 'text.subtleDark' },
        danger: { color: 'error.text' }
      }
    },
    Input: {
      sizes: { small: 9, medium: 10, large: 11 },
      borderRadius: 0,
      paddingX: 1,
      borderColor: 'grays.400',
      borderWidth: '1px',
      '&[aria-invalid="true"]': {
        backgroundColor: 'initial',
        borderColor: 'error.border'
      },
      '&[aria-invalid="true"]:focus:not(:disabled)': {
        backgroundColor: 'initial',
        borderColor: 'error.border'
      },
      ':hover:not(:disabled)': {
        borderColor: 'grays.400'
      }
    },
    FormLabel: {
      color: 'grays.600',
      paddingY: 2
    },
    DialogContent: {
      width: ['100% !important'],
      maxWidth: '600px'
    },
    InputAddon: {
      padding: 0,
      border: 'none'
    },
    InputGroup: {
      // recommended: match sizes of input and buttons so
      // that they go well together in forms next to other
      sizes: { small: 9, medium: 10, large: 11 }
    },
    Adornment: {
      background: 'primary'
    },
    IconButton: {
      background: 'none',
      ':hover, :focus, :active': {
        background: 'none',
        border: 'none'
      }
    }
  }
});

export default { tokens, components };
